import type { SidebarType } from '@/registries/sidebars';

/**
 * The name a draft's state is filed under (TM-9455 / ADR-0029).
 *
 * One string, three shapes, and until this module existed each shape was built and
 * taken apart by hand at the site that needed it:
 *
 * - **active** — `createPatient`. The single `:new` cascade column of a type. Bare,
 *   because there is only ever one at a time (§G).
 * - **instance** — `createPatient#3`. One panel in the draft zone. Minted on
 *   graduation and never reused.
 * - **identity** — `customFormPlayer:form-7:pat-3`. A draft named by what it is OF
 *   rather than by which generation it is (§G-bis).
 *
 * Any of the three may carry a **variant** suffix — `prescription#3/gkv` — for a
 * panel that keeps several slots under one draft (the three payer-domain bodies plus
 * the picker's `flow`). A variant is part of the draft, not a draft of its own:
 * discarding or renaming the root takes every variant with it, which is what
 * {@link draftKeyCovers} and {@link rekeySlot} are for.
 *
 * ## Why this is a type and not a convention
 *
 * The grammar had four separate parsers — `slice(0, indexOf(':'))` in the graduation
 * watcher, `split(':')` in the player's discard, `startsWith(`${key}/`)` in the slot
 * store, `startsWith('customFormPlayer:')` in the registry — and each one knew a
 * different amount about it. That already cost a bug: an identity key built from ids
 * that survive `encodeURIComponent` (a comma among them) was split back into
 * fragments by a separator-joined watcher signal, the key no longer matched itself,
 * and an OPEN questionnaire read as a column that had left the cascade and graduated
 * on the spot.
 *
 * The brand is the other half. A key was previously written inline wherever one was
 * needed — including as `key ?? 'createPatient'` in ten registry entries, where the
 * literal had to equal the entry's own `type` field and nothing checked that it did.
 * `DraftKey` is assignable TO `string` but not FROM one, so those literals stop
 * compiling while every `Map<string, …>`, `Record<string, …>` and `toBe('…')` that
 * holds or asserts a key keeps working untouched.
 */
declare const draftKeyBrand: unique symbol;

export type DraftKey = string & { readonly [draftKeyBrand]: 'DraftKey' };

/** Which of the three shapes a key has — see {@link DraftKey}. */
export type DraftKeyKind = 'active' | 'instance' | 'identity';

const INSTANCE_SEPARATOR = '#';
const IDENTITY_SEPARATOR = ':';
const VARIANT_SEPARATOR = '/';

/**
 * The characters that carry meaning in a key. A type name never contains one
 * (`SidebarType` is camelCase) and neither does a uuid, but an identity is built
 * from ids that arrive as props, so the constructors check rather than trust —
 * a part that smuggled a separator in would produce a key that parses back as
 * something else, which is the failure mode this module exists to end.
 */
const SEPARATORS = [INSTANCE_SEPARATOR, IDENTITY_SEPARATOR, VARIANT_SEPARATOR];

const hasSeparator = (value: string): boolean =>
  SEPARATORS.some((separator) => value.includes(separator));

/**
 * The draft held by the one active `:new` cascade column of this type.
 *
 * Bare type, because a create flow starts as the single active column and there is
 * only ever one of those — the reducer answers a second identical `{type,id}` with a
 * re-trigger rather than a second column (§G). It takes an instance key when it
 * graduates.
 */
export function activeDraftKey(type: SidebarType): DraftKey {
  return type as DraftKey;
}

/**
 * One panel parked in the draft zone.
 *
 * `generation` is a counter that never rewinds and is never reused, so two drafts of
 * one type on one page cannot collide. It is deliberately NOT the number the user
 * sees: that one is positional and renumbers as drafts come and go
 * (`draftStore.ordinalOf`), while this one is the identity behind it.
 */
export function instanceDraftKey(type: SidebarType, generation: number): DraftKey {
  return `${type}${INSTANCE_SEPARATOR}${generation}` as DraftKey;
}

/**
 * A draft named by what it is OF — `customFormPlayer:form-7:pat-3` (§G-bis).
 *
 * `null` when the parts do not identify one: a questionnaire opened with no patient
 * has answers nobody can tell apart either, so there is nothing to file. Callers
 * already treat that as "these props are not a draft", which is why this reports it
 * rather than throwing — a bad prop bag must not white-screen the panel it belongs to.
 *
 * Parts are stringified here rather than by the caller so the emptiness and separator
 * checks cannot be skipped by a `String(x)` at the call site.
 */
export function identityDraftKey(type: SidebarType, ...parts: unknown[]): DraftKey | null {
  if (parts.length === 0) return null;
  const rendered: string[] = [];
  for (const part of parts) {
    if (part === null || part === undefined) return null;
    const value = String(part);
    if (!value || hasSeparator(value)) return null;
    rendered.push(value);
  }
  return `${type}${IDENTITY_SEPARATOR}${rendered.join(IDENTITY_SEPARATOR)}` as DraftKey;
}

/**
 * One slot of a draft that keeps several — `prescription#3/gkv`.
 *
 * A variant is not a draft: it has no dirtiness, no rail and no ✕ of its own, and it
 * lives and dies with its root. Nesting one inside another is refused because the
 * root would then be ambiguous, and every rename and discard walks from the root.
 */
export function variantDraftKey(key: DraftKey, variant: string): DraftKey {
  if (!variant || hasSeparator(variant)) {
    throw new Error(`Invalid draft slot variant: ${JSON.stringify(variant)}`);
  }
  return `${key}${VARIANT_SEPARATOR}${variant}` as DraftKey;
}

/**
 * A well-formed key: a type, optionally a generation or an identity, optionally a
 * variant. Anchored, and every part refuses the separators, so a string can only
 * match the shape it will later be taken apart as.
 */
const DRAFT_KEY_PATTERN = /^[A-Za-z][A-Za-z0-9]*(#[^:#/]+|:[^:#/]+(:[^:#/]+)*)?(\/[^:#/]+)?$/;

/**
 * Take a key back out of a string that has been somewhere untyped — the graduation
 * watcher's JSON signal, or a test naming a draft it expects to exist.
 *
 * A real check, not a cast: the shape is what every reader here relies on, so a
 * string that cannot be one is a programming error and says so at the point it
 * enters rather than as a draft that quietly fails to match itself later. This is
 * the ONLY way a `DraftKey` is made from an arbitrary string — the constructors
 * above are how one is made from its parts, and production code should reach for
 * those.
 */
export function parseDraftKey(raw: string): DraftKey {
  if (!DRAFT_KEY_PATTERN.test(raw)) {
    throw new Error(`Not a draft key: ${JSON.stringify(raw)}`);
  }
  return raw as DraftKey;
}

/** Everything up to the first separator: the part every shape starts with. */
function head(key: string): string {
  let end = key.length;
  for (const separator of SEPARATORS) {
    const index = key.indexOf(separator);
    if (index !== -1 && index < end) end = index;
  }
  return key.slice(0, end);
}

/**
 * Which sidebar this draft belongs to — the one fact all three shapes share, and the
 * only one the graduation watcher needs when it asks whether a type still has an
 * active column.
 */
export function draftKeyType(key: DraftKey): SidebarType {
  return head(key) as SidebarType;
}

export function draftKeyKind(key: DraftKey): DraftKeyKind {
  const rest = key.slice(head(key).length);
  if (rest.startsWith(INSTANCE_SEPARATOR)) return 'instance';
  if (rest.startsWith(IDENTITY_SEPARATOR)) return 'identity';
  return 'active';
}

/**
 * The ids an identity key was built from, in the order they were given — the
 * inverse of {@link identityDraftKey}, and the reason the player can find the
 * answers it must clear when a parked questionnaire is discarded.
 *
 * Empty for the other two shapes, which name a generation rather than a record.
 */
export function identityDraftKeyParts(key: DraftKey): string[] {
  if (draftKeyKind(key) !== 'identity') return [];
  return draftKeyRoot(key).split(IDENTITY_SEPARATOR).slice(1);
}

/**
 * The key without its variant suffix, if it has one. Private: no caller has needed
 * to strip a variant yet, and an exported one would be a second way to ask the
 * questions {@link draftKeyType} and {@link identityDraftKeyParts} already answer.
 */
function draftKeyRoot(key: DraftKey): DraftKey {
  const index = key.indexOf(VARIANT_SEPARATOR);
  return (index === -1 ? key : key.slice(0, index)) as DraftKey;
}

/**
 * Does `slotKey` belong to this draft — is it the draft itself, or one of its
 * variants?
 *
 * The test every discard and every rename walks the slot map with. A prefix compare
 * alone would be wrong: `prescription` must not swallow `prescriptionTemplate`, which
 * is why the variant separator is required rather than merely allowed.
 */
export function draftKeyCovers(key: DraftKey, slotKey: string): boolean {
  return slotKey === key || slotKey.startsWith(`${key}${VARIANT_SEPARATOR}`);
}

/**
 * Move one slot from one draft to another, keeping whichever variant it was under.
 * `null` when the slot does not belong to `from` — so a caller can map over the whole
 * slot map and let this decide what travels.
 *
 * This is graduation seen from the slot store: the active column's `createPatient`
 * and `createPatient/gkv` become `createPatient#3` and `createPatient#3/gkv`.
 */
export function rekeySlot(slotKey: string, from: DraftKey, to: DraftKey): DraftKey | null {
  if (!draftKeyCovers(from, slotKey)) return null;
  return (to + slotKey.slice(from.length)) as DraftKey;
}
